import { FC, useContext, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { VirtualizedGrid } from "@ryde/controls";
import {
  BOOKING_CALENDAR_HEADER_ROW_HEIGHT,
  COURSES_CALENDAR_HOUR_ROW_COUNT,
  coursesCalendarRowsFilterAtom,
  datesArrayCalendarCoursesSF,
  daysCountCalendarCoursesSF,
  filterCoursesCalendarSlotsByRowsFilter,
  getCoursesCalendarVisibleHourIndices,
  ICoursesCalendarSlotDto,
  ScrollAreaEnum,
} from "@ryde/shared";

import { CoursesCalendarCellRenderStrategy } from "./courses-calendar-cell-render-strategy";
import {
  CALENDAR_HOUR_ROW_HEIGHT_PX,
  CALENDAR_TIME_PARSE_RADIX,
  COURSES_CALENDAR_COLUMN_WIDTH_PX,
  COURSES_CALENDAR_HOUR_COLUMN_WIDTH_PX,
} from "./courses-calendar.constants";
import { CoursesCalendarContext } from "./courses-calendar.context";
import {
  CoursesCalendarTooltipContentStyles,
  CoursesCalendarWrapperStyled,
} from "./courses-calendar.styled";

const coursesCalendarSlotCellKey = (slot: ICoursesCalendarSlotDto): string =>
  `${slot.courseId}|${slot.direction}|${slot.dateKey}|${slot.timeLabel}`;

function buildSlotMap(
  slots: ICoursesCalendarSlotDto[],
  dates: string[],
): Map<string, ICoursesCalendarSlotDto[]> {
  const map = new Map<string, ICoursesCalendarSlotDto[]>();
  for (const s of slots) {
    if (!dates.includes(s.dateKey)) {
      continue;
    }
    const h = parseInt(
      s.timeLabel.split(":")[0] ?? "0",
      CALENDAR_TIME_PARSE_RADIX,
    );
    if (Number.isNaN(h) || h < 0 || h >= COURSES_CALENDAR_HOUR_ROW_COUNT) {
      continue;
    }
    const k = `${s.dateKey}|${h}`;
    const list = map.get(k) ?? [];
    const logicalKey = coursesCalendarSlotCellKey(s);
    if (
      list.some(existing => coursesCalendarSlotCellKey(existing) === logicalKey)
    ) {
      continue;
    }
    list.push(s);
    map.set(k, list);
  }
  return map;
}

interface ICoursesCalendarWrapper {
  slots: ICoursesCalendarSlotDto[];
  onCreateSlotRide: (slot: ICoursesCalendarSlotDto) => Promise<number>;
  onAfterSlotProduce?: () => void;
}

export const CoursesCalendarWrapper: FC<ICoursesCalendarWrapper> = ({
  slots,
  onCreateSlotRide,
  onAfterSlotProduce,
}) => {
  const days = useRecoilValue(daysCountCalendarCoursesSF);
  const dateKeys = useRecoilValue(datesArrayCalendarCoursesSF);
  const rowsFilter = useRecoilValue(coursesCalendarRowsFilterAtom);
  const { coursesCalendarWidth, coursesCalendarHeight } = useContext(
    CoursesCalendarContext,
  );

  const visibleHourIndices = useMemo(
    () => getCoursesCalendarVisibleHourIndices({ slots, filter: rowsFilter }),
    [slots, rowsFilter],
  );

  const filteredSlots = useMemo(
    () => filterCoursesCalendarSlotsByRowsFilter({ slots, filter: rowsFilter }),
    [slots, rowsFilter],
  );

  const bodyRowCount = visibleHourIndices.length;
  const cssBodyRowCount = Math.max(bodyRowCount, 1);

  const slotMap = useMemo(
    () => buildSlotMap(filteredSlots, dateKeys),
    [filteredSlots, dateKeys],
  );

  const gridData = useMemo(
    () => ({
      dateKeys,
      visibleHourIndices,
      slotMap,
      onCreateSlotRide,
      onAfterSlotProduce,
    }),
    [
      dateKeys,
      visibleHourIndices,
      slotMap,
      onCreateSlotRide,
      onAfterSlotProduce,
    ],
  );

  return (
    <>
      <CoursesCalendarTooltipContentStyles />
      <CoursesCalendarWrapperStyled
        $rowsCount={cssBodyRowCount}
        $daysCount={days}
      >
        <VirtualizedGrid
          scrollArea={ScrollAreaEnum.CoursesCalendar}
          height={coursesCalendarHeight}
          width={coursesCalendarWidth}
          frozenRowCount={1}
          frozenColumnCount={1}
          columnCount={dateKeys.length + 1}
          rowCount={bodyRowCount + 1}
          columnWidth={columnIndex =>
            columnIndex === 0
              ? COURSES_CALENDAR_HOUR_COLUMN_WIDTH_PX
              : COURSES_CALENDAR_COLUMN_WIDTH_PX
          }
          rowHeight={index =>
            index === 0
              ? BOOKING_CALENDAR_HEADER_ROW_HEIGHT
              : CALENDAR_HOUR_ROW_HEIGHT_PX
          }
          gridData={gridData}
          CellRenderer={CoursesCalendarCellRenderStrategy}
        />
      </CoursesCalendarWrapperStyled>
    </>
  );
};
